import "./style.scss";

import { FileDownloadOutlined, PrintOutlined } from "@mui/icons-material";
import {
    isMilestoneThresholdAmountBased,
    resolveMilestoneBaseColumn,
} from "containers/TermFormContainer/ChannelsStep/milestoneBaseColumnUtils";
import type { ITitle } from "containers/Titles/types";
import { formatLocaleDateString, getCurrencySymbol } from "helpers/utils";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { fetchTerm } from "store/bpTermStore";
import type { RootState } from "store/store";
import { useAppDispatch } from "store/store";

import ChannelIcon from "../ChannelIcon";

type TProps = {
    term: {
        id: number;
        advancePayment: number;
        compensate: number;
        created: string;
        modified: string;
        name: string;
        surname: string;
        reference: string;
        representative: string | null;
        beneficiarId: number;
        companyId: number;
        representativeId: number;
        titleId: number;
        originalFilemime: string | null;
        originalFilename: string | null;
        originalFilesize: string | null;
        endDate: Date | null;
    };
    title: ITitle;
    companies: {
        id: number;
        name: string;
        avatarUrl: string;
        currency?: string;
    }[];
};

const BPTermDetail = ({ term, companies, title }: TProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const termData = useSelector((state: RootState) => state.bpTerm.term);

    const company = companies.find((comp) => comp.id === term.companyId);
    const currencySymbol = getCurrencySymbol(company?.currency || "EUR");

    useEffect(() => {
        if (term && term.id) {
            dispatch(fetchTerm(term.id));
        }
    }, [dispatch, term]);

    return (
        <div className="bp-term-detail-container">
            <div className="detail-header">
                <div className="detail-persons">
                    <p className="subtitle">
                        {t("Titles.Term")} n° {term.reference ? term.reference : "-"}
                    </p>
                    <p className="company">
                        <span className="grey">{t("BPTitle.Publisher")} : </span>
                        {company?.avatarUrl && <img src={company?.avatarUrl} alt="avatar-company" />}
                        {company?.name}
                    </p>
                    <p className="title">
                        <span className="grey">
                            {t("ShowDetails.EndDate", { date: formatLocaleDateString({ date: term.endDate ?? "" }) })}
                        </span>
                    </p>
                    {term.representative && (
                        <p>
                            <span className="grey">{t("Beneficiars.AssociatedPerson")} : </span>
                            {term.representative}
                        </p>
                    )}
                </div>
            </div>
            <div className="detail-sales">
                <h3>{t("Terms.Stage")}</h3>
                {termData?.channelData.map((channel, i) => {
                    // Thresholds are stored in the unit of the column the contract crosses them on:
                    // monetary columns in cents, quantity as copies sold.
                    const milestoneBaseColumn = resolveMilestoneBaseColumn(
                        channel.milestoneBaseColumn,
                        channel.calculationBaseColumn ?? "quantity",
                    );
                    const thresholdsAreAmounts = isMilestoneThresholdAmountBased(milestoneBaseColumn);
                    const milestoneThresholdDivider = thresholdsAreAmounts ? 100 : 1;

                    return (
                        <div className="detail-channel" key={`channel-${i}`}>
                            <div className="detail-channel-title">
                                <ChannelIcon channelKind={channel.kind} />
                                {channel.channelName}
                            </div>
                            <div className="detail-channel-body">
                                <div className="bloc-milestones">
                                    {channel.data.map((milestone, j) => {
                                        return (
                                            <div className="detail-channel-milestones" key={`channel-data-${i}-${j}`}>
                                                <div>
                                                    <p className="milestones-amount">
                                                        {`${
                                                            j > 0
                                                                ? +channel.data[j - 1].quantity /
                                                                  milestoneThresholdDivider
                                                                : 0
                                                        }`}
                                                        {` ${t("SendEmail.To").toLowerCase().replace(":", "")} `}
                                                        {milestone.quantity === "Infinity"
                                                            ? `${t("Terms.Infinity")}`
                                                            : `${+milestone.quantity / milestoneThresholdDivider}`}
                                                    </p>
                                                    <p className="milestones-type">{`${
                                                        thresholdsAreAmounts
                                                            ? `${t("BPDashboard.From").toLowerCase()} ${t(
                                                                  "NewChannel.Amount",
                                                              )} ${t("BPDashboard.In")} ${currencySymbol}`
                                                            : t("Summary.Sales").toLowerCase() + "s"
                                                    }`}</p>
                                                </div>
                                                <div>
                                                    <p className="milestones-amount">{`${milestone.percentage}%`}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            {false && (
                <div className="detail-actions">
                    <button>
                        <PrintOutlined />
                        {t("ShowStatements.Print")}
                    </button>
                    <button>
                        <FileDownloadOutlined />
                        {t("ShowStatements.Download")}
                    </button>
                </div>
            )}
        </div>
    );
};

export default BPTermDetail;
